import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Money } from "@/components/Money";
import { FilterBar } from "@/components/filters/FilterBar";
import { SalesTrendChart, SummaryCards, TopBars } from "@/components/sales/SalesCharts";
import { useSource } from "@/context/SourceContext";
import { apiError } from "@/lib/api";
import { useFilters } from "@/lib/filters";
import { fmtDate, fmtQty } from "@/lib/format";
import { QUICK_RANGES } from "@/lib/ranges";
import { cn } from "@/lib/utils";

const COLS = [["date", "Date", "date"], ["invoice_no", "Invoice no"], ["customer", "Customer", "customer"], ["salesman", "Salesperson", "salesman"],
  ["item", "Item / Product"], ["qty", "Qty"], ["rate", "Rate"], ["amount", "Amount (ex-VAT)", "amount"], ["cost", "Cost"], ["gp_value", "GP"]];
const NUM = ["qty", "rate", "amount"];
const ALL_HISTORY = ["2000-01-01", new Date().toISOString().slice(0, 10)];

const SortBtn = ({ col, sort, onSort }) => (
  <button type="button" onClick={() => onSort(col[2])} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`sales-sort-${col[2]}`}>
    {col[1]} {sort.key !== col[2] ? <ArrowUpDown className="h-3 w-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
  </button>
);

const LinesTable = ({ data, summary, sort, onSort, onPage }) => (
  <div className="rise-in overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="sales-lines-table">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <tr>{COLS.map((c) => <th key={c[0]} className={cn("whitespace-nowrap px-3 py-3", NUM.includes(c[0]) ? "text-right" : "text-left")}>{c[2] ? <SortBtn col={c} sort={sort} onSort={onSort} /> : c[1]}</th>)}</tr>
        </thead>
        <tbody>
          {data?.rows.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-500" data-testid="sales-empty">No sales match these filters</td></tr>}
          {data?.rows.map((r) => (
            <tr key={r.id} className="row-hover border-t border-slate-100" data-testid={`sales-row-${r.id}`}>
              <td className="num whitespace-nowrap px-3 py-2">{fmtDate(r.date)}</td>
              <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">{r.invoice_no || "—"}</td>
              <td className="max-w-[220px] truncate px-3 py-2 text-slate-800" title={r.customer} data-blur>{r.customer || "—"}</td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-700">{r.salesman || "—"}</td>
              <td className="max-w-[220px] truncate px-3 py-2 text-slate-700" title={r.item}>{r.item || "—"}{r.is_return && <span className="ml-1.5 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700">Return</span>}</td>
              <td className="num px-3 py-2 text-right">{r.qty == null ? "—" : fmtQty(r.qty)}</td>
              <td className="num px-3 py-2 text-right">{r.rate == null ? "—" : r.rate.toFixed(3)}</td>
              <td className="px-3 py-2 text-right"><Money value={r.amount} /></td>
              <td className="px-3 py-2 text-right">{r.cost == null ? "—" : <Money value={r.cost} />}</td>
              <td className="px-3 py-2 text-right">{r.gp_value == null ? "—" : <Money value={r.gp_value} />}</td>
            </tr>
          ))}
        </tbody>
        {data && (
          <tfoot>
            <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="sales-totals-row">
              <td colSpan={7} className="px-3 py-3">Total of {data.count.toLocaleString()} filtered rows (all pages)</td>
              <td className="px-3 py-3 text-right" data-testid="sales-total-amount"><Money value={summary?.net_sales ?? 0} /></td>
              <td className="px-3 py-3 text-right" data-testid="sales-total-cost"><Money value={summary?.cost ?? 0} /></td>
              <td className="px-3 py-3 text-right" data-testid="sales-total-gp"><Money value={summary?.gp ?? 0} /></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    {data && (
      <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-4 py-2 text-xs text-slate-600">
        <span data-testid="sales-page-info">Page {data.page} of {data.pages.toLocaleString()}</span>
        <Button variant="outline" size="icon" className="h-7 w-7" disabled={data.page <= 1} onClick={() => onPage(data.page - 1)} data-testid="sales-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" className="h-7 w-7" disabled={data.page >= data.pages} onClick={() => onPage(data.page + 1)} data-testid="sales-next-page"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    )}
  </div>
);

export default function SalesAnalysisPage() {
  const src = useSource();
  const fl = useFilters(useMemo(() => QUICK_RANGES[3].range(), []));
  const [gran, setGran] = useState("monthly");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [page, setPage] = useState(1);
  const [analysis, setAnalysis] = useState(null);
  const [lines, setLines] = useState(null);
  const filterKey = fl.key;
  useEffect(() => setPage(1), [filterKey]);

  useEffect(() => { src.get(`/sales/analysis?${fl.query({ granularity: gran })}`).then((r) => setAnalysis(r.data)).catch((e) => toast.error(apiError(e))); }, [src, filterKey, gran]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { src.get(`/sales/lines?${fl.query({ sort: sort.key, dir: sort.dir, page, page_size: 50 })}`).then((r) => setLines(r.data)).catch((e) => toast.error(apiError(e))); }, [src, filterKey, sort, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (key) => { setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" })); setPage(1); };
  const pick = (field, name) => !fl.f[field].includes(name) && fl.update({ [field]: [...fl.f[field], name] });
  return (
    <div className="space-y-6" data-testid="sales-analysis-page">
      <PageHeader brand eyebrow="Revenue" title="Sales Analysis" subtitle="Sales report lines · amounts ex-VAT in Omani Rial · returns netted (shown as Return rows and in the Returns card)" />
      <FilterBar fl={fl} periodLabel="Last 12 months" allHistory={ALL_HISTORY} />
      <SummaryCards s={analysis?.summary} status={analysis?.status} />
      {analysis?.gp_note && <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="sales-gp-note">{analysis.gp_note}: {analysis.gp_missing_months.join(", ")}</p>}
      <SalesTrendChart data={analysis?.trend} granularity={gran} onGranularity={setGran} />
      <div className="grid gap-6 xl:grid-cols-2">
        <TopBars title="Top customers" data={analysis?.top_customers} onPick={(n) => pick("customer", n)} testid="top-customers-chart" color="#0D9488" />
        <TopBars title="Top salespersons" data={analysis?.top_salesmen} onPick={(n) => pick("salesman", n)} testid="top-salesmen-chart" color="#1E3A5F" />
      </div>
      <LinesTable data={lines} summary={analysis?.summary} sort={sort} onSort={onSort} onPage={setPage} />
    </div>
  );
}
